import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

export interface OpportunityItem {
  auditKey: string
  title: string
  titleFr?: string
  displayValue: string | null
  savingsMs: number | null
  savingsBytes: number | null
  url?: string
  description?: string
  detailsJson?: any
  pagesCount?: number
}

interface OpportunityAccordionProps {
  opportunities: OpportunityItem[]
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

function formatResourceUrl(url: string): string {
  try {
    const parsed = new URL(url)
    // Show pathname + filename, skip origin for readability
    const path = parsed.pathname
    const query = parsed.search ? '?' + parsed.search.slice(1, 40) : ''
    return path + query
  } catch {
    return url
  }
}

function SavingsBadge({ savingsMs, savingsBytes }: { savingsMs: number | null; savingsBytes: number | null }) {
  if (savingsMs && savingsMs > 0) {
    return (
      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-600 whitespace-nowrap">
        -{formatMs(savingsMs)}
      </span>
    )
  }
  if (savingsBytes && savingsBytes > 0) {
    return (
      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-600 whitespace-nowrap">
        -{formatBytes(savingsBytes)}
      </span>
    )
  }
  return null
}

interface DetailItem {
  url?: string
  totalBytes?: number
  wastedBytes?: number
  wastedMs?: number
  [key: string]: any
}

function DetailsTable({ items }: { items: DetailItem[] }) {
  const visible = items.slice(0, 10)
  return (
    <div className="mt-2 overflow-x-auto rounded border border-light-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-[#F4F7FE] text-[#555A73]">
            <th className="text-left px-2 py-1.5 font-medium">URL</th>
            <th className="text-right px-2 py-1.5 font-medium whitespace-nowrap">Taille</th>
            <th className="text-right px-2 py-1.5 font-medium whitespace-nowrap">Gain</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((item, idx) => {
            const rowUrl = item.url ?? item.label ?? ''
            const size = item.totalBytes != null ? formatBytes(item.totalBytes) : '—'
            const gain =
              item.wastedMs != null && item.wastedMs > 0
                ? formatMs(item.wastedMs)
                : item.wastedBytes != null && item.wastedBytes > 0
                ? formatBytes(item.wastedBytes)
                : '—'
            return (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFD]'}>
                <td className="px-2 py-1.5 text-[#1B2559] max-w-[400px]">
                  <a
                    href={rowUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-[#4318FF] hover:underline"
                    title={rowUrl}
                  >
                    {formatResourceUrl(rowUrl)}
                  </a>
                </td>
                <td className="px-2 py-1.5 text-right text-[#555A73] whitespace-nowrap">{size}</td>
                <td className="px-2 py-1.5 text-right text-orange-600 whitespace-nowrap font-medium">{gain}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {items.length > 10 && (
        <p className="px-2 py-1 text-xs text-[#8B8FA7] bg-[#FAFBFD] border-t border-light-border">
          +{items.length - 10} éléments supplémentaires
        </p>
      )}
    </div>
  )
}

function parseDetailsItems(detailsJson: any): DetailItem[] | null {
  if (!detailsJson) return null
  if (Array.isArray(detailsJson)) return detailsJson
  // Format Lighthouse : { type: 'table', items: [...] }
  if (detailsJson.items && Array.isArray(detailsJson.items)) return detailsJson.items
  return null
}

export function OpportunityAccordion({ opportunities }: OpportunityAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  if (!opportunities || opportunities.length === 0) return null

  const sorted = [...opportunities].sort((a, b) => {
    const msA = a.savingsMs ?? 0
    const msB = b.savingsMs ?? 0
    return msB - msA
  })

  function handleToggle(idx: number) {
    setOpenIndex(prev => (prev === idx ? null : idx))
  }

  return (
    <div className="rounded-lg border border-light-border overflow-hidden divide-y divide-light-border">
      {sorted.map((opp, idx) => {
        const isOpen = openIndex === idx
        const label = opp.titleFr ?? opp.title
        const detailItems = parseDetailsItems(opp.detailsJson)

        return (
          <div key={opp.auditKey}>
            {/* Header */}
            <button
              type="button"
              onClick={() => handleToggle(idx)}
              className="w-full flex items-center gap-2 px-4 py-3 bg-white hover:bg-[#F4F7FE] cursor-pointer transition-colors text-left"
            >
              <span className="flex-shrink-0 text-[#8B8FA7]">
                {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </span>
              <span className="flex-1 text-sm font-medium text-[#1B2559] leading-snug">
                {label}
                {opp.pagesCount && opp.pagesCount > 1 && (
                  <span className="ml-2 text-xs font-normal text-[#8B8FA7]">
                    sur {opp.pagesCount} pages
                  </span>
                )}
              </span>
              <SavingsBadge savingsMs={opp.savingsMs} savingsBytes={opp.savingsBytes} />
            </button>

            {/* Body */}
            {isOpen && (
              <div className="px-4 py-3 bg-[#FAFBFD]">
                {opp.description && (
                  <p className="text-xs text-[#555A73] mb-2 leading-relaxed">{opp.description}</p>
                )}

                {opp.displayValue && (
                  <p className="text-xs text-[#555A73] mb-2">
                    <span className="font-medium text-[#1B2559]">Valeur mesurée :</span> {opp.displayValue}
                  </p>
                )}

                {opp.url && (
                  <p className="text-xs text-[#555A73] mb-2">
                    <span className="font-medium text-[#1B2559]">Page concernée :</span>{' '}
                    <a
                      href={opp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#4318FF] hover:underline break-all"
                    >
                      {opp.url}
                    </a>
                  </p>
                )}

                {detailItems && detailItems.length > 0 && (
                  <DetailsTable items={detailItems} />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
